/**
 * MarketingNotFoundPage — the ONE marketing-site 404 (PALM-WEB-404-MOBILE-OVERFLOW-001).
 *
 * Every Colony marketing router ends in `{ path: '*' }`, and the nine sites had grown four different
 * 404 layouts: five a flex row of large buttons that could not wrap (palm-web scrolled sideways at
 * 375 px), two a second class family for the same page, one an empty-state with two link lists, and
 * port-web's links in the header's nav list, which hides below 1024 px — so a phone saw no links at
 * all. Owner ruling Q-PW404-1 ("Each app keeps its words"): one layout everywhere, each site keeping
 * its own wording and destinations. So this component holds NO copy and knows NO app — the host
 * passes its translated words and its links — and it draws with shared-styles' ONE `.not-found-*`
 * family, whose link row wraps on a tablet and stacks full width at ≤767 px.
 *
 * It is the page's LAYOUT, not the page: the host page keeps its own `<MarketingSeo>` (head + JSON-LD)
 * and its own `trackPageView`, as every marketing page does — the website rules
 * (`require-document-head`, `require-structured-data`, `require-page-view-tracking`) judge the PAGE
 * file, so a page that delegated them here would carry no evidence of either.
 *
 * Links render through `<SmartLink>`, so an internal destination routes in the app and an outbound
 * one stays a real link. The first link is the primary action; every other one is secondary.
 *
 * @module @puffin-core/marketing-shell/MarketingNotFoundPage
 */

import PropTypes from 'prop-types';

import { SmartLink } from '../links/SmartLink.jsx';

const PRIMARY_LINK_CLASS = 'btn btn-marketing-lg btn-marketing-primary';
const SECONDARY_LINK_CLASS = 'btn btn-marketing-lg btn-marketing-secondary';

/**
 * @typedef {Object} NotFoundLink
 * @property {string} href - where the link goes (an app route or an outbound URL)
 * @property {string} label - the host's translated label
 */

/**
 * @param {Object} props
 * @param {string} props.code - the eyebrow above the title (the host's translated "404")
 * @param {string} props.title - the page's one level-1 heading
 * @param {string} props.message - what happened, in the host's words
 * @param {NotFoundLink[]} props.links - at least one destination, each once; the first is primary
 * @param {string} props.linksAriaLabel - the accessible name of the links' navigation
 * @returns {React.JSX.Element}
 */
export function MarketingNotFoundPage({ code, title, message, links, linksAriaLabel }) {
  return (
    <section className="not-found-page" data-testid="marketing-not-found-page">
      <p className="not-found-code">{code}</p>
      <h1 className="not-found-title">{title}</h1>
      <p className="not-found-message">{message}</p>
      <nav aria-label={linksAriaLabel}>
        <ul className="not-found-links">
          {links.map((link, index) => (
            <li key={link.href}>
              <SmartLink
                href={link.href}
                className={index === 0 ? PRIMARY_LINK_CLASS : SECONDARY_LINK_CLASS}
              >
                {link.label}
              </SmartLink>
            </li>
          ))}
        </ul>
      </nav>
    </section>
  );
}

/**
 * The link list's contract: at least one link (a 404 with nowhere to go is the defect this component
 * exists to end), each with an href and a label, and no destination twice (the href is the row key).
 *
 * @param {Object} props
 * @param {string} propName
 * @param {string} componentName
 * @returns {Error | null}
 */
function notFoundLinksPropType(props, propName, componentName) {
  const links = props[propName];
  if (!Array.isArray(links) || links.length === 0) {
    return new Error(`${componentName}: \`${propName}\` must hold at least one link.`);
  }
  const seen = new Set();
  for (const link of links) {
    if (
      !link ||
      typeof link.href !== 'string' ||
      link.href === '' ||
      typeof link.label !== 'string' ||
      link.label === ''
    ) {
      return new Error(
        `${componentName}: every entry of \`${propName}\` needs an href and a label.`,
      );
    }
    if (seen.has(link.href)) {
      return new Error(`${componentName}: \`${propName}\` lists "${link.href}" twice.`);
    }
    seen.add(link.href);
  }
  return null;
}

/**
 * Required at the prop, then judged by the link contract.
 *
 * @param {Object} props
 * @param {string} propName
 * @param {string} componentName
 * @returns {Error | null}
 */
function requiredNotFoundLinks(props, propName, componentName) {
  if (props[propName] === undefined || props[propName] === null) {
    return new Error(`${componentName}: \`${propName}\` is required.`);
  }
  return notFoundLinksPropType(props, propName, componentName);
}

MarketingNotFoundPage.propTypes = {
  code: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  links: requiredNotFoundLinks,
  linksAriaLabel: PropTypes.string.isRequired,
};

export default MarketingNotFoundPage;
